import { useEffect, useSyncExternalStore } from 'react';
import { useBlocker, useLocation } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

/**
 * Global "unsaved changes" navigation guard.
 *
 * Restores the behaviour of the legacy tool's
 * `resources/views/layouts/shared/footer-script.blade.php`, which bound a
 * `beforeunload` handler that warned the user before leaving a page whose
 * `<form>` had been edited. Here we:
 *
 *   - Treat any page that contains a `<form>` as guarded (matching legacy).
 *   - Mark the page "dirty" when the user edits a form field (`input`/`change`).
 *     Programmatic value changes (e.g. React populating a form from a fetch)
 *     do not dispatch these DOM events, so loading data never trips the guard.
 *   - Suppress the warning while a form is being submitted (the save itself).
 *   - Honour a global `#disableAlert` element as an escape hatch, exactly like
 *     the legacy markup, plus a per-form `data-disable-unsaved-alert` opt-out.
 *   - Warn on browser-level navigation (refresh / tab close / external links)
 *     via `beforeunload`, and on in-app SPA navigation via `useBlocker`.
 *
 * The legacy copy is preserved verbatim so users see the same wording.
 */
const UNSAVED_MESSAGE = 'You have unsaved changes which will not be saved.';

let dirty = false;
let submitting = false;
const subscribers = new Set<() => void>();

function emit() {
  for (const fn of subscribers) fn();
}

function subscribe(fn: () => void) {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

function alertsDisabled() {
  return document.getElementById('disableAlert') !== null;
}

function markDirty() {
  if (!dirty) {
    dirty = true;
    emit();
  }
}

function markSubmitting() {
  submitting = true;
  if (dirty) {
    dirty = false;
    emit();
  }
}

function reset() {
  if (dirty || submitting) {
    dirty = false;
    submitting = false;
    emit();
  }
}

/**
 * Clear the unsaved-changes flag. Editing pages that persist via a button +
 * programmatic `navigate()` (rather than a native form submit) should call
 * this on a successful save so the guard does not prompt during that
 * save-and-navigate.
 */
export function clearUnsavedChanges() {
  reset();
}

export function NavigationGuard() {
  const location = useLocation();
  const isDirty = useSyncExternalStore(subscribe, () => dirty);

  // Reset the baseline whenever a navigation completes — every page starts
  // "clean", mirroring the legacy per-page-load capture of the form state.
  useEffect(() => {
    reset();
  }, [location.key]);

  // Wire global form-edit/submit detection once for the app lifetime.
  useEffect(() => {
    const onEdit = (event: Event) => {
      if (alertsDisabled()) return;
      const target = event.target as HTMLElement | null;
      // Arm on edits inside a real <form> (legacy parity) or inside any region
      // explicitly opting in via `data-unsaved-guard` — the React editing pages
      // use controlled inputs without a <form> wrapper, so they mark the region
      // instead. List/search/filter pages stay unguarded (no false prompts).
      const region = target?.closest?.('form, [data-unsaved-guard]');
      if (!region) return;
      if (region.hasAttribute('data-disable-unsaved-alert')) return;
      markDirty();
    };
    const onSubmit = () => markSubmitting();

    document.addEventListener('input', onEdit, true);
    document.addEventListener('change', onEdit, true);
    document.addEventListener('submit', onSubmit, true);
    return () => {
      document.removeEventListener('input', onEdit, true);
      document.removeEventListener('change', onEdit, true);
      document.removeEventListener('submit', onSubmit, true);
    };
  }, []);

  // Browser-level navigation (refresh, tab close, external links).
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (!isDirty || submitting || alertsDisabled()) return;
      event.preventDefault();
      // Modern browsers show their own generic copy; older ones display this.
      event.returnValue = UNSAVED_MESSAGE;
      return UNSAVED_MESSAGE;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // In-app SPA navigation (clicking links, programmatic navigate()).
  //
  // Read the live module-level `dirty`/`submitting` flags rather than the
  // `isDirty` React snapshot. Editing pages that save via a button call
  // `clearUnsavedChanges()` synchronously right before `navigate()`. That
  // clears `dirty` immediately, but the React re-render (and therefore any
  // captured `isDirty` closure) has not committed yet — so a stale snapshot
  // would wrongly block the save-and-navigate and strand the page in its
  // "Saving…" state when the user chose to stay. Reading the live flags keeps
  // the guard in sync with the current save.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      !submitting &&
      !alertsDisabled() &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  const blocked = blocker.state === 'blocked';

  return (
    <Modal show={blocked} onHide={() => blocker.reset?.()} centered>
      <Modal.Header closeButton>
        <Modal.Title>Leave this page?</Modal.Title>
      </Modal.Header>
      <Modal.Body>{UNSAVED_MESSAGE}</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => blocker.reset?.()}>
          Stay on page
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            reset();
            blocker.proceed?.();
          }}
        >
          Leave page
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
